/**
 * Values both sides need to agree on. A constant duplicated in the client and the server
 * is a constant that will eventually disagree.
 */

export const MEMBERSHIP_ROLES = ["owner", "admin", "member"] as const;
export type MembershipRole = (typeof MEMBERSHIP_ROLES)[number];

/** Ordered for comparison: member < admin < owner. */
export const ROLE_RANK: Record<MembershipRole, number> = { member: 1, admin: 2, owner: 3 };

/**
 * Whether `role` meets or exceeds `minimum`. The one place the ordering is applied, so a
 * server gate and the button the client shows for it can never disagree — which is what
 * `role === "owner" || role === "admin"`, written out at each call site, eventually does.
 */
export const hasRole = (role: MembershipRole, minimum: MembershipRole): boolean =>
  ROLE_RANK[role] >= ROLE_RANK[minimum];

export const AUTH_PROVIDERS = ["password", "magic_link", "google"] as const;
export type AuthProvider = (typeof AUTH_PROVIDERS)[number];

/**
 * Error codes the client branches on. Anything not listed should be shown as a generic
 * failure rather than special-cased on a string the server might rename.
 */
export const ERROR_CODES = {
  validation: "validation_error",
  unauthenticated: "unauthenticated",
  forbidden: "forbidden",
  notFound: "not_found",
  rateLimited: "rate_limited",
  invalidCsrf: "invalid_csrf",
  invalidOrigin: "invalid_origin",
  accountExists: "account_exists",
  invalidCredentials: "invalid_credentials",
  magicLinkInvalid: "magic_link_invalid",
  lastIdentity: "last_identity",
  lastOwner: "last_owner",
  internal: "internal_error",
  billingNotConfigured: "billing_not_configured",
  billingUnavailable: "billing_unavailable",
  subscriptionRequired: "subscription_required",
  billingBlocked: "billing_blocked",
  alreadySubscribed: "already_subscribed",
  noSubscription: "no_subscription",
  cardDeclined: "card_declined",
  emailRequired: "email_required",
  placeTaken: "place_taken",
  placeCodeRequired: "place_code_required",
  placeTakenSanta: "place_taken_santa",
  signInRequired: "sign_in_required",
  sessionConflict: "session_conflict",
  mergeAvailable: "merge_available",
  placeConflict: "place_conflict",
  groupClosed: "group_closed",
  groupFull: "group_full",
  santaTooFew: "santa_too_few",
  santaImpossible: "santa_impossible",
  redrawConfirm: "redraw_confirm",
  redrawRequired: "redraw_required",
} as const;

export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];

export const LIMITS = {
  passwordMin: 12,
  passwordMax: 128,
  nameMin: 2,
  nameMax: 100,
  emailMax: 254,
  descriptionMax: 2000,
  listTitleMax: 120,
  wishTitleMax: 200,
  wishNoteMax: 1000,
  wishQuantityMax: 50,
  urlMax: 2048,
  guestNameMax: 60,
  deliveryAddressMax: 500,
  listsPerUser: 1000,
  /** A group is a family or a circle of friends; fifty covers the biggest Christmas. */
  placesPerGroup: 50,
  groupsPerUser: 20,
  groupTitleMax: 60,
  /** A first name, or "Mamie Jo": short, and never room for a sentence or a link. */
  personNameMax: 40,
  /** A bound on a number that ends up in other people's inboxes, not a business rule. */
  santaBudgetMaxCents: 100_000,
  santaMinParticipants: 3,
  /** The organizer's note in Secret Santa mails: a sentence or two, not a letter. */
  santaMessageMax: 255,
  wishesPerList: 300,
  searchQueryMax: 100,
  assistantMessageMax: 2000,
  assistantHistoryMax: 20,
  /** Wishes a product needs across all lists before it gets a public page. */
  productPublicThreshold: 3,
  reviewBodyMax: 1500,
  /** Reports from distinct people that hide a review until someone looks at it. */
  reviewReportsToHide: 3,
  pageSizeDefault: 25,
  pageSizeMax: 100,
  /**
   * Upload ceiling, enforced twice: the schema rejects a larger declared size, and the
   * presigned PUT is signed with the declared Content-Length, so a client that lies about
   * the size has its upload refused by the bucket. Images are for pages, not archives: a
   * phone photo fits well under this.
   */
  uploadSizeMax: 10 * 1024 * 1024,
  filenameMax: 255,
} as const;

/**
 * Whether a product page goes to search engines. A catalogue pick with neither reviews
 * nor lists holding it is a shop's product sheet copied over: it stays reachable from the
 * gift guides, but listing thousands of them makes Google judge the whole site thin and
 * delay the pages that matter. The sitemap query mirrors this rule.
 */
export const isProductIndexable = (product: {
  wishCount: number;
  rating: { count: number } | null;
}): boolean =>
  (product.rating?.count ?? 0) > 0 || product.wishCount >= LIMITS.productPublicThreshold;

/**
 * First-party cookie remembering where a visitor came from until they sign up: the shared
 * list or group they opened, and the utm_source (or referring site) and campaign. Written
 * by the browser (apps/web/src/lib/attribution.ts), read once by the server when the
 * account is created (apps/server/src/auth/acquisition.ts), then deleted. Audience
 * measurement only, never shared, hence its short life.
 */
export const ACQUISITION_COOKIE = "listou_src";
export const ACQUISITION_COOKIE_DAYS = 30;
/** The cookie's fields, as URL search params: list slug, group slug, source, campaign. */
export const ACQUISITION_KEYS = { list: "l", group: "g", source: "s", campaign: "c" } as const;
export const ACQUISITION_VALUE_MAX = 64;

/**
 * What a browser may upload.
 *
 * An allowlist, not a blocklist: the interesting types to reject are the ones nobody
 * thought of. The declared type is also only a claim — a client can send any bytes under
 * any of these — so this narrows what the app will store and serve, and nothing more.
 */
export const ALLOWED_UPLOAD_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "application/pdf",
  "text/plain",
  "text/csv",
] as const;

export type AllowedUploadType = (typeof ALLOWED_UPLOAD_TYPES)[number];

/**
 * The one-time code emailed alongside the sign-in link, for people who would rather type
 * something than click.
 *
 * Six digits, so the code can be typed on a numeric keypad and read back over the phone
 * without spelling anything out. That is a million codes — ~20 bits, which is low.
 *
 * The entropy is not what protects this; the attempt cap is. Five guesses against a
 * million is one chance in 200,000, and exhausting them burns the code, so guessing costs
 * a fresh email — of which the server allows five per address per hour. Widening the
 * alphabet would buy entropy this design does not spend, at the cost of a code that has
 * to be transcribed character by character.
 */
export const AUTH_CODE_ALPHABET = "0123456789";
export const AUTH_CODE_LENGTH = 6;

/** Attempts allowed against one code before it is burned and a new email is required. */
export const AUTH_CODE_MAX_ATTEMPTS = 5;

/**
 * Normalises a code the way a person actually types it: the spaces or dashes that come
 * from reading it back in groups. Shared so the client cleans input exactly as the server
 * compares it — a mismatch here would reject a code the user typed correctly.
 *
 * Digits have no look-alikes to fold: anything that is not a digit is a character the user
 * did not read off the email, and the schema rejects it rather than guessing at it.
 */
export const normalizeAuthCode = (value: string): string => value.replace(/[\s-]/g, "");

// --- Billing -----------------------------------------------------------------

/**
 * The two billing intervals this boilerplate ships with. Anything beyond monthly/annual
 * (usage-based, per-seat tiers) is a new plan catalogue, not a new interval here.
 */
export const BILLING_INTERVALS = ["monthly", "annual"] as const;
export type BillingInterval = (typeof BILLING_INTERVALS)[number];

/**
 * Subscription status as this app understands it, which is deliberately NOT Stripe's list.
 *
 * Stripe has eight statuses that answer "what is the billing machine doing"; the app only
 * ever asks "does this workspace have access, and should it see a warning". Mapping at the
 * boundary means a new Stripe status cannot silently become an unhandled branch in a
 * feature gate — `stripe/plans.ts` has to place it.
 *
 * `revoked` has no Stripe equivalent: it is what a refund, a dispute or a fraud warning
 * leaves behind, and it must outrank whatever Stripe still reports about the subscription.
 */
export const SUBSCRIPTION_STATUSES = [
  "trialing",
  "active",
  /** Payment failed but Stripe is still retrying: keep access, show a warning. */
  "past_due",
  "canceled",
  /** Checkout never completed, or the first payment never cleared. */
  "incomplete",
  /** Cut off by us after a refund, dispute or fraud warning. */
  "revoked",
] as const;
export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUSES)[number];

/**
 * Statuses that grant access to paid features. `past_due` is included on purpose: Stripe
 * retries a failed payment for days, and locking someone out on the first retry loses
 * customers whose card simply expired.
 */
export const ACTIVE_SUBSCRIPTION_STATUSES = [
  "trialing",
  "active",
  "past_due",
] as const satisfies readonly SubscriptionStatus[];

export const isActiveStatus = (status: SubscriptionStatus): boolean =>
  (ACTIVE_SUBSCRIPTION_STATUSES as readonly SubscriptionStatus[]).includes(status);

/** Why access was revoked. Kept so support can tell a chargeback from a refund. */
export const REVOCATION_REASONS = ["refund", "dispute", "fraud_warning"] as const;
export type RevocationReason = (typeof REVOCATION_REASONS)[number];

// --- Lists & wishes --------------------------------------------------------------

/** What the list is for. Drives the default theme and the copy on the public page. */
export const LIST_OCCASIONS = ["christmas", "birthday", "birth", "wedding", "other"] as const;
export type ListOccasion = (typeof LIST_OCCASIONS)[number];

/**
 * The order people choose an occasion in. Apart from LIST_OCCASIONS, which also defines the
 * Postgres enum: reordering that one would change the schema to change a menu.
 */
export const OCCASION_CHOICES: readonly ListOccasion[] = [
  "christmas",
  "birth",
  "birthday",
  "wedding",
  "other",
];

/**
 * Who can open the public page. `link` is the default: anyone holding the slug, no account.
 * `private` keeps the list to the workspace while it is being drafted.
 */
export const LIST_VISIBILITIES = ["private", "link"] as const;
export type ListVisibility = (typeof LIST_VISIBILITIES)[number];

/**
 * Visual presets for the public page. Stored as text, not a Postgres enum: themes will be
 * added and retired far more often than the schema should migrate, and an unknown value
 * falls back to the occasion's default instead of failing a read.
 */
export const LIST_THEMES = [
  "classic",
  "christmas",
  "kids",
  "birth",
  "wedding",
  "minimal",
  "sauge",
  "lavande",
  "peche",
  "dragee",
  "menthe",
  "terracotta",
  "nuit",
] as const;
export type ListTheme = (typeof LIST_THEMES)[number];

export const isListTheme = (value: string): value is ListTheme =>
  (LIST_THEMES as readonly string[]).includes(value);

export type ThemePalette = {
  label: string;
  background: string;
  surface: string;
  ink: string;
  muted: string;
  accent: string;
  /** Dark palettes flip the page: light text on a deep background. */
  dark?: boolean;
};

/**
 * Colour palettes. Shared rather than CSS-only because the server draws with them too:
 * the Open Graph and story images must match the page they link to. Each accent is
 * picked to carry white button text; the web app still darkens it if a custom accent
 * does not.
 *
 * The first six ids predate the labels and are kept for existing lists.
 */
export const THEME_PALETTES: Record<ListTheme, ThemePalette> = {
  classic: {
    label: "Papier",
    background: "#fbf6ef",
    surface: "#fffdf9",
    ink: "#2d241c",
    muted: "#8b7b6b",
    accent: "#cf5a41",
  },
  christmas: {
    label: "Sapin",
    background: "#f3f0e6",
    surface: "#fffdf8",
    ink: "#1e3a2f",
    muted: "#6e7f74",
    accent: "#b3322b",
  },
  kids: {
    label: "Soleil",
    background: "#fff4d6",
    surface: "#fffdf6",
    ink: "#2b2552",
    muted: "#7a7399",
    accent: "#e8613a",
  },
  birth: {
    label: "Nuage",
    background: "#edf3f8",
    surface: "#fdfeff",
    ink: "#24384d",
    muted: "#6f879c",
    accent: "#4f84b0",
  },
  wedding: {
    label: "Lin",
    background: "#f6f1e9",
    surface: "#fffdf9",
    ink: "#3b3129",
    muted: "#95887a",
    accent: "#9c7644",
  },
  minimal: {
    label: "Encre",
    background: "#f6f6f4",
    surface: "#ffffff",
    ink: "#1a1a1a",
    muted: "#6b6b6b",
    accent: "#1a1a1a",
  },
  sauge: {
    label: "Sauge",
    background: "#ecf1e8",
    surface: "#fbfcf8",
    ink: "#27352b",
    muted: "#6f7d70",
    accent: "#557f5b",
  },
  lavande: {
    label: "Lavande",
    background: "#f2eef8",
    surface: "#fdfcff",
    ink: "#2e2744",
    muted: "#7d7596",
    accent: "#7a5fb8",
  },
  peche: {
    label: "Pêche",
    background: "#fff0e8",
    surface: "#fffaf7",
    ink: "#45261d",
    muted: "#9a7465",
    accent: "#dd6f52",
  },
  dragee: {
    label: "Dragée",
    background: "#fbeef2",
    surface: "#fffafb",
    ink: "#4a2432",
    muted: "#9d7482",
    accent: "#c95c7c",
  },
  menthe: {
    label: "Menthe",
    background: "#e9f5f1",
    surface: "#fbfefd",
    ink: "#1f3b36",
    muted: "#6a8a84",
    accent: "#23857a",
  },
  terracotta: {
    label: "Terracotta",
    background: "#f5e9df",
    surface: "#fffaf5",
    ink: "#3f2419",
    muted: "#93705f",
    accent: "#b8522f",
  },
  nuit: {
    label: "Nuit étoilée",
    background: "#1d2240",
    surface: "#282e52",
    ink: "#f6f1e4",
    muted: "#b5b2ca",
    accent: "#e9b949",
    dark: true,
  },
};

/**
 * Page templates: layout, card shape, hero and typography together, not a colour swap.
 * The palette (THEME_PALETTES) and the motif sit on top of whichever template is chosen.
 */
export const LIST_TEMPLATES = [
  "carnet",
  "vitrine",
  "faire-part",
  "pop",
  "mosaique",
  "epure",
] as const;
export type ListTemplate = (typeof LIST_TEMPLATES)[number];

export const TEMPLATE_CONFIG: Record<
  ListTemplate,
  {
    label: string;
    description: string;
    /** Starts without a motif: on a wall of product photos, stickers behind them are noise. */
    plainBackground?: boolean;
  }
> = {
  carnet: {
    label: "Carnet",
    description: "Scrapbook : cartes polaroid inclinées, masking tape, écriture manuscrite.",
  },
  vitrine: {
    label: "Vitrine",
    description: "Magazine : grande photo de couverture, typo éditoriale, grille aérée.",
    plainBackground: true,
  },
  "faire-part": {
    label: "Faire-part",
    description: "Élégant et centré, cadre en arche, parfait pour un mariage ou une naissance.",
  },
  pop: {
    label: "Pop",
    description: "Aplats de couleur, grosse typo, stickers partout. Idéal pour les enfants.",
  },
  mosaique: {
    label: "Mosaïque",
    description: "De grandes photos, toutes de la même taille, et rien autour.",
    plainBackground: true,
  },
  epure: { label: "Épuré", description: "Sobre et lisible, rien que les envies." },
};

/** Typography pairs (display + text). Loaded by the web app; the ids are the contract. */
export const LIST_FONTS = ["ronde", "elegante", "manuscrite", "editoriale"] as const;
export type ListFont = (typeof LIST_FONTS)[number];

/**
 * What each template looks like without an explicit font choice. Shared: the admin counts
 * the font a page actually shows, not only the ones picked by hand.
 */
export const TEMPLATE_DEFAULT_FONT: Record<ListTemplate, ListFont> = {
  carnet: "manuscrite",
  vitrine: "editoriale",
  "faire-part": "elegante",
  pop: "ronde",
  mosaique: "editoriale",
  epure: "ronde",
};

/** A spouse or co-parent is an owner; a helper who only edits is an editor. */
/**
 * What a group of lists is for, fixed at creation.
 * - `lists`: several people behind one link, each claiming a place and filling its list.
 * - `secret_santa`: the same, plus a draw that tells each participant whom they give to.
 * - `collection`: one person's lists behind one link ("Déco": the bedroom, the kitchen).
 *   Nobody claims anything; visitors browse and give.
 */
export const GROUP_MODES = ["lists", "secret_santa", "collection"] as const;
export type GroupMode = (typeof GROUP_MODES)[number];

/**
 * The gommettes of a group: coloured dots stuck on people, like the ones on a parcel.
 * Same gommette, same circle (a household, a couple, a team at work): by default they do
 * not draw each other. Stored as keys; the page draws them.
 */
export const GROUP_COLORS = {
  framboise: "#e0356a",
  sapin: "#1f6b52",
  ocean: "#2563a8",
  soleil: "#c98a00",
  lilas: "#7b4fb3",
  mandarine: "#d8612a",
  menthe: "#1c8a8a",
  ardoise: "#4a5068",
} as const;
export type GroupColor = keyof typeof GROUP_COLORS;
export const GROUP_COLOR_KEYS = Object.keys(GROUP_COLORS) as GroupColor[];

/** How French names each gommette, for labels and screen readers. */
export const GROUP_COLOR_LABELS: Record<GroupColor, string> = {
  framboise: "framboise",
  sapin: "sapin",
  ocean: "océan",
  soleil: "soleil",
  lilas: "lilas",
  mandarine: "mandarine",
  menthe: "menthe",
  ardoise: "ardoise",
};

export const LIST_MEMBER_ROLES = ["owner", "editor"] as const;
export type ListMemberRole = (typeof LIST_MEMBER_ROLES)[number];

/**
 * Everything that changes with the occasion: wording, default look, who the co-owner is.
 * One table rather than `if (occasion === ...)` scattered through the UI and the mails.
 * Emoji are written as escapes so the source stays plain ASCII.
 */
export const OCCASION_CONFIG: Record<
  ListOccasion,
  {
    label: string;
    emoji: string;
    defaultTheme: ListTheme;
    defaultTemplate: ListTemplate;
    /** Sticker pack decorating a new list (see stickers.ts). */
    defaultStickerPack: string;
    /** The title of a list nobody named, so it must fit any list: never an example. */
    defaultTitle: string;
    /** What the recipient field is called, when the list is for someone else. */
    recipientLabel: string | null;
    /** How the person invited as a co-owner is called in the invite form and mail. */
    coOwnerLabel: string;
    /** Headline on the public page, `{name}` replaced by the recipient or the owners. */
    publicHeadline: string;
    /** Who a review helps, as in "Votre avis aidera {reviewHelps} à choisir". */
    reviewHelps: string;
  }
> = {
  christmas: {
    label: "No\u00ebl",
    emoji: "\u{1F384}",
    defaultTheme: "christmas",
    defaultTemplate: "carnet",
    defaultStickerPack: "noel",
    defaultTitle: "Ma liste de No\u00ebl",
    recipientLabel: "Pour qui ? (un enfant, par exemple)",
    coOwnerLabel: "co-organisateur",
    publicHeadline: "La liste de No\u00ebl de {name}",
    reviewHelps: "d'autres familles qui pr\u00e9parent No\u00ebl",
  },
  birthday: {
    label: "Anniversaire",
    emoji: "\u{1F382}",
    defaultTheme: "kids",
    defaultTemplate: "pop",
    defaultStickerPack: "fete",
    defaultTitle: "Mon anniversaire",
    recipientLabel: "Qui f\u00eate son anniversaire ?",
    coOwnerLabel: "co-organisateur",
    publicHeadline: "L'anniversaire de {name}",
    reviewHelps: "d'autres personnes qui pr\u00e9parent un anniversaire",
  },
  birth: {
    label: "Naissance",
    emoji: "\u{1F476}",
    defaultTheme: "birth",
    defaultTemplate: "mosaique",
    defaultStickerPack: "bebe",
    defaultTitle: "Notre liste de naissance",
    recipientLabel: "Pr\u00e9nom du b\u00e9b\u00e9 (si vous le connaissez)",
    coOwnerLabel: "co-parent",
    publicHeadline: "La liste de naissance de {name}",
    reviewHelps: "d'autres jeunes parents",
  },
  wedding: {
    label: "Mariage",
    emoji: "\u{1F48D}",
    defaultTheme: "wedding",
    defaultTemplate: "faire-part",
    defaultStickerPack: "mariage",
    defaultTitle: "Notre liste de mariage",
    recipientLabel: null,
    coOwnerLabel: "conjoint(e)",
    publicHeadline: "La liste de mariage de {name}",
    reviewHelps: "d'autres futurs mari\u00e9s",
  },
  other: {
    label: "Autre",
    emoji: "\u{1F381}",
    defaultTheme: "classic",
    defaultTemplate: "vitrine",
    defaultStickerPack: "jardin",
    defaultTitle: "Mes envies",
    recipientLabel: "Pour qui ?",
    coOwnerLabel: "co-organisateur",
    publicHeadline: "Les envies de {name}",
    reviewHelps: "d'autres personnes en qu\u00eate du bon cadeau",
  },
};

/** Coup de coeur / envie / idée, highest first. */
export const WISH_PRIORITIES = ["love", "want", "idea"] as const;
export type WishPriority = (typeof WISH_PRIORITIES)[number];

/**
 * Where the metadata fetch for a pasted link stands. `none` means the wish was typed in
 * by hand and there is nothing to fetch.
 */
export const SCRAPE_STATUSES = ["none", "pending", "done", "failed"] as const;
export type ScrapeStatus = (typeof SCRAPE_STATUSES)[number];

/**
 * Whether a display name is the placeholder made at sign-up from the address
 * ("baudet.clement" for baudet.clement@…). Such a name is never shown as a signature: a
 * list signed "baudet.clement" reads like a bug, not like a person.
 */
export const isPlaceholderName = (name: string, email: string | null): boolean =>
  // Exact, not case-insensitive: the default is the local part verbatim, while a name
  // typed by hand ("Bob" for bob@…) is capitalised and must show. A guest has no address,
  // and its name is the first name it typed.
  email !== null && name.trim() === (email.split("@")[0] ?? "");

/**
 * The title a list starts with when nobody typed one. Only a starting point: the page shows
 * the stored title, so an owner can always rename it. Clipped because a long name plus the
 * occasion's phrase can exceed what the title field accepts.
 */
export const defaultListTitle = (occasion: ListOccasion, recipientName: string | null): string =>
  recipientName
    ? OCCASION_CONFIG[occasion].publicHeadline
        .replace("{name}", () => recipientName)
        .slice(0, LIMITS.listTitleMax)
    : OCCASION_CONFIG[occasion].defaultTitle;

/**
 * The rating star, in a 24x24 box: rounded tips, drawn the same on the page and in the
 * share images.
 */
export const STAR_PATH =
  "M11.01 3.7Q12 1.7 12.99 3.7L14.83 7.44Q15.29 8.37 16.32 8.52L20.44 9.12Q22.65 9.44 21.05 11L18.07 13.9Q17.33 14.63 17.5 15.66L18.21 19.76Q18.58 21.96 16.61 20.92L12.92 18.98Q12 18.5 11.08 18.98L7.39 20.92Q5.42 21.96 5.79 19.76L6.5 15.66Q6.67 14.63 5.93 13.9L2.95 11Q1.35 9.44 3.56 9.12L7.68 8.52Q8.71 8.37 9.17 7.44Z";

/** The filled star's colour; an empty one is the ink at low opacity. */
export const STAR_COLOR = "#f2a922";

/**
 * The competitors with a comparison page (`/comparatif/<slug>`). Shared because the sitemap
 * lists them and the web renders them: a page missing from one side is either unreachable
 * for crawlers or a sitemap entry that 404s.
 */
export const COMPARISON_SLUGS = ["mesenvies", "milirose", "kadolog", "amazon"] as const;
export type ComparisonSlug = (typeof COMPARISON_SLUGS)[number];

/**
 * The blog posts (`/blog/<slug>`). Shared for the same reason as the comparisons: the
 * sitemap lists them and the web renders them. Slugs carry no year, so a post refreshed for
 * next year keeps its URL and its ranking.
 */
export const BLOG_SLUGS = [
  "meilleure-liste-de-naissance",
  "meilleure-liste-anniversaire",
  "meilleure-liste-de-noel",
  "meilleur-site-secret-santa",
  "pere-noel-secret-regles-budget",
  "secret-santa-entre-collegues",
  "idees-cadeaux-secret-santa",
  "que-mettre-liste-de-naissance",
  "liste-de-naissance-quand-partager",
  "pere-noel-secret-en-famille",
  "creer-liste-de-noel-en-ligne",
  "eviter-doublons-cadeaux-noel",
  "liste-de-noel-enfant-idees-par-age",
  "lettre-au-pere-noel",
  "liste-de-naissance-gratuite-multi-enseignes",
] as const;
export type BlogSlug = (typeof BLOG_SLUGS)[number];
